import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Button,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Tooltip,
  colors
} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { Label } from 'components';

const useStyles = makeStyles(theme => ({
  root: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  content: {
    padding: 0
  },
  inner: {
    minWidth: 700
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end'
  }
}));

const Results = props => {
  const { className, group, onShareRepository, title, ...rest } = props;

  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setOpen(false);
  }, [group])

  const handleExpand = () => {
    setOpen(!open);
  };

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = event => {
    setPage(0);
    setRowsPerPage(event.target.value);
  };

  const repositories = group.allRepositories.map(repo => ({
    ...repo,
    shared: group.repositories.some(shared => shared.id === repo.id)
  }))

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <Card elevation={5}>
        <CardHeader
          action={
            <Tooltip title={(open ? 'Hide' : 'View') + ' Repositories'}>
              <IconButton onClick={handleExpand}>
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          }
          subheader={
            <Typography
              color="textSecondary"
              variant="body2"
            >
              {repositories.length} Records found. Page {page + 1} of{' '}
              {Math.ceil(repositories.length / rowsPerPage)}
            </Typography>
          }
          title={title}
        />
        <Divider />
        <Collapse in={open}>
          <CardContent className={classes.content}>
            <PerfectScrollbar>
              <div className={classes.inner}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repositories.slice(0, rowsPerPage).map(repository => {
                      const { shared } = repository;
                      return (
                        <TableRow
                          hover
                          key={repository.id}
                          selected={shared}
                        >
                          <TableCell>{repository.name}</TableCell>
                          <TableCell>{repository.description || '-'}</TableCell>
                          <TableCell align="center">
                            <Label color={shared ? colors.green[500] : colors.red[500]}>
                              {shared ? 'shared' : 'unshared'}
                            </Label>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              color="primary"
                              endIcon={<ShareIcon />}
                              onClick={() => onShareRepository(shared, repository.id)}
                              size="small"
                              variant={shared ? 'outlined' : 'contained'}
                            >
                              {shared ? 'unshare' : 'share'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>
          </CardContent>
        </Collapse>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={repositories.length}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      </Card>
    </div>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  group: PropTypes.object.isRequired,
  onShareRepository: PropTypes.func,
  title: PropTypes.string,
};

Results.defaultProps = {
  group: {
    allRepositories: [],
    repositories: []
  }
};

export default Results;
